import type {
  FinanceRate,
  Job,
  WorkType,
} from '@kanda-libs/ks-frontend-services';
import { useJobContext } from 'pages/Job/context';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  calculateJobTotal,
  formatLenderConfigs,
  getCurrentInfoWorkType,
  getRateCost,
  getValidFinanceRates,
  type FormattedLenderConfig,
} from 'utils';
import { checkJobPaidByOB, getJobDepositPayment } from 'utils/Job-functions';

export type JobFinanceOptionsDisplayType = 'all' | 'selected';

export interface JobFinanceOptionsHook {
  job?: Job;
  type: JobFinanceOptionsDisplayType;
  lenderConfigs: FormattedLenderConfig | undefined;
  rates: FinanceRate[] | undefined;
  total: number;
  deposit: number;
  loan: number;
  subsidy?: number;
  payout?: number;
  classNames: {
    card: string;
    deposit: string;
  };
  acknowledged: boolean;
  showBankingPayout: boolean;
  showFinancePayout: boolean;
  onAcknowledgeClick: () => void;
  isLoading: boolean;
  paidByOB: boolean;
  workType?: WorkType;
  isCashDeposit: boolean;
  showOpenBanking: boolean;
}

export default function useJobFinanceOptions(): JobFinanceOptionsHook {
  const { job, company, infoWorkTypes, isLoading } = useJobContext();

  const [acknowledged, setAcknowledged] = useState<boolean>(false);

  const paidByOB = useMemo(() => {
    if (!job) return false;
    return checkJobPaidByOB(job);
  }, [job]);

  const showOpenBanking = useMemo(() => {
    if (!company) return false;
    return false;
    // return !company?.hide_openbanking;
  }, [company]);

  const type = useMemo(() => {
    if (isLoading) return 'all';
    if (paidByOB) return 'selected';
    const financeStatus = job?.finance_status;
    if (financeStatus === 'not_applied') return 'all';
    const option = job?.customer_options?.finance_option;
    return option ? 'selected' : 'all';
  }, [isLoading, job, paidByOB]);

  const lenderConfigs = useMemo(
    () => (company ? formatLenderConfigs(company) : undefined),
    [company],
  );

  const workType = useMemo(() => job?.work_type, [job]);

  const total = useMemo(
    () => (job?.job_items ? calculateJobTotal(job.job_items).totalIncVat : 0),
    [job],
  );

  const deposit = useMemo(
    () =>
      job?.customer_options?.deposit_value?.amount ||
      job?.deposit_value?.amount ||
      0,
    [job],
  );

  const companyRates = useMemo(() => company?.finance_rates, [company]);

  const infoWorkType = useMemo(
    () => getCurrentInfoWorkType(companyRates, infoWorkTypes, workType),
    [companyRates, infoWorkTypes, workType],
  );

  const allRates = useMemo(
    () => getValidFinanceRates(infoWorkType, total, deposit),
    [infoWorkType, total, deposit],
  );

  const rates = useMemo(() => {
    if (isLoading) return [];
    if (type === 'all') return allRates;
    const option = job?.customer_options?.finance_option;
    return option ? [option] : undefined;
  }, [isLoading, allRates, type, job]);

  const subsidy = useMemo(() => {
    if (type === 'all' || !rates) return undefined;
    const rate = rates[0];
    return getRateCost(rate, total, deposit);
  }, [rates, type, total, deposit]);

  const loan = useMemo(() => total - deposit, [total, deposit]);

  const payout = useMemo(
    () => (subsidy !== undefined ? loan - subsidy : undefined),
    [loan, subsidy],
  );

  const depositPayment = useMemo(() => getJobDepositPayment(job), [job]);

  const higherDeposit = useMemo(() => {
    if (type === 'all') return false;
    const customerDeposit = job?.customer_options?.deposit_value?.amount;
    if (!customerDeposit || !depositPayment) return false;
    if (
      depositPayment?.payment_option.payment_method === 'bank' ||
      depositPayment?.payment_option.payment_method === 'card'
    )
      return false;
    const setDeposit = job?.deposit_value?.amount || 0;
    return customerDeposit !== setDeposit;
  }, [type, job, depositPayment]);

  const isCashDeposit = useMemo(
    () => depositPayment?.payment_option.payment_method === 'cash',
    [depositPayment],
  );

  const classNames = useMemo(
    () => ({
      card: higherDeposit && !acknowledged ? 'order-1' : 'order-3',
      deposit: higherDeposit ? 'order-1 mb-2' : 'order-3',
    }),
    [higherDeposit, acknowledged],
  );

  const jobIdDepositKey = useMemo(() => {
    const id = job?.id;
    if (!id) return undefined;
    return `job-${id}-deposit-warning-acknowledged`;
  }, [job]);

  const onAcknowledgeClick = useCallback(() => {
    setAcknowledged(true);
    if (!jobIdDepositKey) return;
    window.localStorage.setItem(jobIdDepositKey, 'true');
  }, [jobIdDepositKey]);

  const showFinancePayout = useMemo(() => {
    if (isLoading || !job) return false;
    if (type === 'all') return false;
    return job.customer_options?.checkout_option === 'apply_for_finance';
  }, [job, isLoading, type]);

  const showBankingPayout = useMemo(() => {
    if (isLoading || !job) return false;
    if (type === 'all') return false;
    return paidByOB;
  }, [job, paidByOB, type, isLoading]);

  useEffect(() => {
    if (!jobIdDepositKey) return;
    const previouslyAcknowledged =
      window.localStorage.getItem(jobIdDepositKey) === 'true';
    if (!previouslyAcknowledged) return;
    setAcknowledged(true);
  }, [jobIdDepositKey]);

  return {
    job,
    type,
    lenderConfigs,
    rates,
    total,
    deposit,
    loan,
    subsidy,
    payout,
    classNames,
    acknowledged,
    onAcknowledgeClick,
    showBankingPayout,
    showFinancePayout,
    isLoading,
    paidByOB,
    workType,
    isCashDeposit,
    showOpenBanking,
  };
}
